import { motion } from "framer-motion";
import { ArrowLeft, Shield, Heart, Zap, Brain, GraduationCap, Building, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { Navbar } from "@/components/navbar";
import { Footer } from "@/components/footer";
import { EdgeGlow } from "@/components/edge-glow";

const teamMembers = [
  {
    id: "rebecca-francis",
    name: "Rebecca Francis",
    role: "Founder & CEO",
    type: "founder",
    icon: Heart,
    color: "#14b8a6",
    imageUrl: "https://evcdikzpnjjpotbkkshs.supabase.co/storage/v1/object/public/public-bucket/RebeccaFrancis.jpeg",
    shortBio: "Diagnosed with refractory catamenial status epilepticus epilepsy in 2019. Faced with a lack of tracking tools for seizures and cycles, she set out to build the solution herself.",
    fullBio: "Diagnosed with refractory catamenial status epilepticus epilepsy in 2019. Faced with a lack of tracking tools for seizures and cycles, she set out to build the solution herself. She began by meticulously tracking symptoms, treatments, and patterns, searching for better ways to manage and predict neurological health challenges. Rebecca recognised the critical need for patient-driven health platforms connecting lived experience to actionable insights and research outcomes.",
    credentials: [
      "Award-winning innovator with over 10 years' entrepreneurial experience",
      "$42M USD valuation on CommuniKids (EQVista 409a, 2024)",
      "Alumni of CSIRO Innovate to Grow, TRW Investment Ready Program, and Impact10x Accelerator",
      "Backed by TIF Sponsorship (2025) and recognised by industry leaders globally"
    ],
    mission: "Rebecca's mission: to ensure no seizure, tremor, or symptom ever goes unseen again.",
    linkedIn: "https://www.linkedin.com/in/becfrancis/"
  },
  {
    id: "joseph-skewes",
    name: "Joseph Skewes",
    role: "Co-Founder & Head of Patient Ecosystem",
    type: "founder",
    icon: Brain,
    color: "#06B6D4",
    imageUrl: "https://evcdikzpnjjpotbkkshs.supabase.co/storage/v1/object/public/public-bucket/JosephSkewes.jpeg",
    shortBio: "Lived experience with MND. Diagnosed at 40. Saw how limited the treatment options were and went into action.",
    fullBio: "Lived experience with MND. Diagnosed at 40. Saw how limited the treatment options were and went into action. He started tracking therapies, supplements, and symptoms, searching for potential ways to slow progression. Joseph recognised the power of community data and the need for patient-driven health platforms that connect lived experience with research outcomes.",
    credentials: [
      "15+ years in technology and product innovation across large private enterprises, public service, and startups",
      "Alumni of Venture Catalyst, ThincLab, Unboxd, eChallenge, and SAYES",
      "Driven by empathy, curiosity, and a refusal to wait for someone else to build what patients need"
    ],
    vision: "Joseph's vision: empower every patient to become an active participant in their health journey.",
    linkedIn: "https://www.linkedin.com/in/josephskewes/"
  },
  {
    id: "tiago-ferreira",
    name: "Tiago Ferreira",
    role: "Cybersecurity & Certified Lead",
    type: "advisor",
    icon: Shield,
    color: "#8B5CF6",
    imageUrl: "https://evcdikzpnjjpotbkkshs.supabase.co/storage/v1/object/public/public-bucket/tiago.JPEG",
    shortBio: "Background: 20+ years with AFP, INTERPOL. Founder: North Star Strategies. Global advisor to governments and private sectors on cybersecurity and smart infrastructure.",
    fullBio: "Tiago Ferreira brings 20+ years of experience with the Australian Federal Police (AFP) and INTERPOL to his role as Cybersecurity & Certified Lead. As the founder of North Star Strategies, he serves as a global advisor to governments and private sectors on cybersecurity and smart infrastructure. Tiago has spearheaded global 'Smart Cities' initiatives and believes that robust cybersecurity is essential for protecting the trust patients place in digital health platforms."
  },
  {
    id: "liz-keen",
    name: "Liz Keen",
    role: "Clinical Governance Advisor",
    type: "advisor",
    icon: Heart,
    color: "#14b8a6",
    imageUrl: "https://evcdikzpnjjpotbkkshs.supabase.co/storage/v1/object/public/public-bucket/lizKeen.jpg",
    shortBio: "LIZ KEEN, RN. Current: Head of Clinical Governance at InfoSys (AU). Previous: Director at Bupa (AU). Delivered Australia's first Clinical Governance Framework for Digital Health.",
    fullBio: "Liz Keen, RN is a global executive nurse leader currently serving as Head of Clinical Governance at InfoSys (AU). Previously, she was Director at Bupa (AU) and delivered Australia's first Clinical Governance Framework for Digital Health. Liz is passionate about safe, patient-centred care in the digital age and brings extensive expertise in clinical governance and digital health integration."
  },
  {
    id: "michael-visser",
    name: "Dr. Michael Visser",
    role: "Clinical Education Advisor",
    type: "advisor",
    icon: Brain,
    color: "#A855F7",
    imageUrl: "https://evcdikzpnjjpotbkkshs.supabase.co/storage/v1/object/public/public-bucket/michaelVisser.JPEG",
    shortBio: "Role: Associate Lecturer at UQ. Founder: The Project Love App. Award-winning educator, co-authored Australia's first wellbeing guideline for perioperative nurses.",
    fullBio: "Dr. Michael Visser serves as Clinical Education Advisor with his role as Associate Lecturer at UQ. As the founder of The Project Love App, he is an award-winning educator who co-authored Australia's first wellbeing guideline for perioperative nurses. Dr. Visser has led UN/WHO lobbying for end-of-life care and champions compassion in healthcare, ensuring every patient journey is met with dignity and understanding."
  },
  {
    id: "peyman-obeidy",
    name: "Dr. Peyman Obeidy",
    role: "Founding Biomedical Advisor",
    type: "advisor",
    icon: Brain,
    color: "#06B6D4",
    imageUrl: "https://evcdikzpnjjpotbkkshs.supabase.co/storage/v1/object/public/public-bucket/PeymanObeidy.jpg",
    shortBio: "Education: Sydney University & Harvard Graduate. Awards: REDI Fellowship Recipient. Co-author on various biomedical research papers published in leading medical journals.",
    fullBio: "Dr. Peyman Obeidy serves as Founding Biomedical Advisor with education from Sydney University & Harvard Graduate. As a REDI Fellowship Recipient, he is co-author on various biomedical research papers published in leading medical journals. Dr. Obeidy is driven by innovation that bridges cutting-edge science with meaningful patient outcomes, bringing expertise in biomedical engineering and AI from his background at Harvard Medical School and the University of Sydney."
  }
];

export default function Team() {
  const founders = teamMembers.filter(member => member.type === "founder");
  const advisors = teamMembers.filter(member => member.type === "advisor");

  return (
    <div className="bg-white dark:bg-black text-black dark:text-white min-h-screen relative">
      <EdgeGlow />
      <Navbar />
      
      <div className="container mx-auto px-6 py-20">
        {/* Header */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Meet the <span className="text-teal-800 dark:text-teal-400">Dream Team</span>
          </h1>
          <p className="text-xl text-black dark:text-white max-w-4xl mx-auto mb-8">
            World-class experts united by a shared vision to revolutionise healthcare through AI-powered triage and advocacy. Our team combines deep healthcare expertise, cutting-edge technology, and lived experience to build solutions that truly serve patients and caregivers.
          </p>
          <Link href="/">
            <Button
              variant="outline"
              className="border-[#14b8a6] text-black dark:text-white hover:bg-[#14b8a6] hover:text-black hover:shadow-2xl transition-all"
            >
              <ArrowLeft size={16} className="mr-2" />
              Back to Home
            </Button>
          </Link>
        </motion.div>

        {/* Founders Section */}
        <motion.div 
          className="mb-20"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">
              <span className="text-teal-800 dark:text-teal-400">Founders</span>
            </h2>
            <p className="text-black dark:text-white max-w-2xl mx-auto">
              The driving force behind Elevita AI, combining lived experience with world-class expertise.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {founders.map((member, index) => (
              <motion.div
                key={member.id}
                className="p-12 rounded-xl text-black dark:text-white"
                style={{
                  background: document.documentElement.classList.contains('dark')
                    ? member.color === "#14b8a6" 
                      ? 'linear-gradient(135deg, rgba(20, 184, 166, 0.40) 0%, rgba(6, 182, 212, 0.35) 100%)'
                      : member.color === "#06B6D4"
                      ? 'linear-gradient(135deg, rgba(6, 182, 212, 0.40) 0%, rgba(8, 145, 178, 0.35) 100%)'
                      : 'linear-gradient(135deg, rgba(168, 85, 247, 0.40) 0%, rgba(147, 51, 234, 0.35) 100%)'
                    : member.color === "#14b8a6" 
                      ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(240, 253, 255, 0.95) 100%)'
                      : member.color === "#06B6D4"
                      ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(236, 254, 255, 0.95) 100%)'
                      : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(250, 245, 255, 0.95) 100%)',
                  backdropFilter: 'blur(12px)',
                  border: member.color === "#14b8a6" ? '3px solid rgba(20, 184, 166, 0.6)' :
                          member.color === "#06B6D4" ? '3px solid rgba(6, 182, 212, 0.6)' :
                          '3px solid rgba(168, 85, 247, 0.6)',
                  boxShadow: member.color === "#14b8a6" ? '0 12px 48px rgba(20, 184, 166, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.1)' :
                           member.color === "#06B6D4" ? '0 12px 48px rgba(6, 182, 212, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.1)' :
                           '0 12px 48px rgba(168, 85, 247, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
                }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 + index * 0.1 }}
              >
                <div className="flex items-start space-x-8">
                  <div 
                    className="w-48 h-48 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden"
                    style={{ 
                      border: `3px solid ${member.color}`,
                      filter: 'drop-shadow(0 0 30px rgba(20, 184, 166, 0.5)) drop-shadow(0 0 50px rgba(168, 85, 247, 0.4))',
                      background: `radial-gradient(circle, ${member.color}30, transparent)`
                    }}
                  >
                    {member.imageUrl ? (
                      <img 
                        src={member.imageUrl} 
                        alt={member.name} 
                        className="w-full h-full object-cover rounded-full"
                        onError={(e) => {
                          // Fallback to icon if image fails to load
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          const iconContainer = target.parentElement;
                          if (iconContainer) {
                            const icon = document.createElement('div');
                            icon.innerHTML = `<${member.icon.name} size={64} style={{ color: '${member.color}' }} />`;
                            iconContainer.appendChild(icon);
                          }
                        }}
                      />
                    ) : (
                      <member.icon size={64} style={{ color: member.color }} />
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-3xl font-bold mb-3" style={{ color: member.color }}>
                      {member.name}
                    </h3>
                    <p className="text-black dark:text-white font-medium mb-4 text-lg">{member.role}</p>
                    <p className="text-black dark:text-white leading-relaxed mb-6">
                      {member.fullBio}
                    </p>
                    <div className="space-y-2">
                      <h4 className="text-sm font-semibold text-teal-700 dark:text-teal-400 mb-2">Key Credentials:</h4>
                      <ul className="space-y-1">
                        {member.credentials?.map((credential, i) => (
                          <li key={i} className="text-sm text-black dark:text-white flex items-center">
                            <GraduationCap size={12} className="mr-2 text-[#14b8a6]" />
                            {credential}
                          </li>
                        )) || []}
                      </ul>
                    </div>
                    
                    {(member.mission || member.vision) && (
                      <div className="mt-4 p-3 rounded-lg" style={{
                        background: 'linear-gradient(135deg, rgba(20, 184, 166, 0.30), rgba(168, 85, 247, 0.25))',
                        border: '1px solid rgba(20, 184, 166, 0.2)',
                        backdropFilter: 'blur(4px)'
                      }}>
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 italic">
                          {member.mission || member.vision}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Advisors Section */}
        <motion.div 
          className="mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">
              <span className="text-teal-700 dark:text-teal-400">Expert Advisory Board</span>
            </h2>
            <p className="text-black dark:text-white max-w-2xl mx-auto">
              Industry leaders bringing decades of experience across cybersecurity, clinical governance, global growth, and healthcare innovation.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {advisors.map((member, index) => (
              <motion.div
                key={member.id}
                className="p-8 rounded-xl text-black dark:text-white"
                style={{
                  background: member.color === "#14b8a6" 
                    ? 'linear-gradient(135deg, rgba(20, 184, 166, 0.35) 0%, rgba(6, 182, 212, 0.30) 100%)'
                    : member.color === "#06B6D4"
                    ? 'linear-gradient(135deg, rgba(6, 182, 212, 0.35) 0%, rgba(8, 145, 178, 0.30) 100%)'
                    : member.color === "#8B5CF6"
                    ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.35) 0%, rgba(168, 85, 247, 0.30) 100%)'
                    : 'linear-gradient(135deg, rgba(168, 85, 247, 0.35) 0%, rgba(147, 51, 234, 0.30) 100%)',
                  backdropFilter: 'blur(12px)',
                  border: member.color === "#14b8a6" ? '1px solid rgba(20, 184, 166, 0.3)' :
                          member.color === "#06B6D4" ? '1px solid rgba(6, 182, 212, 0.3)' :
                          member.color === "#8B5CF6" ? '1px solid rgba(99, 102, 241, 0.3)' :
                          '1px solid rgba(168, 85, 247, 0.3)',
                  boxShadow: member.color === "#14b8a6" ? '0 12px 40px rgba(20, 184, 166, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.08)' :
                           member.color === "#06B6D4" ? '0 12px 40px rgba(6, 182, 212, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.08)' :
                           member.color === "#8B5CF6" ? '0 12px 40px rgba(99, 102, 241, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.06)' :
                           '0 12px 40px rgba(168, 85, 247, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.08)'
                }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.5 + index * 0.1 }}
              >
                <div className="flex items-start space-x-8">
                  <div 
                    className="w-32 h-32 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden"
                    style={{ 
                      border: `2px solid ${member.color}`,
                      filter: 'drop-shadow(0 0 25px rgba(20, 184, 166, 0.4)) drop-shadow(0 0 35px rgba(168, 85, 247, 0.3))',
                      background: `radial-gradient(circle, ${member.color}25, transparent)`
                    }}
                  >
                    {member.imageUrl ? (
                      <img 
                        src={member.imageUrl} 
                        alt={member.name} 
                        className="w-full h-full object-cover rounded-full"
                        onError={(e) => {
                          // Fallback to icon if image fails to load
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          const iconContainer = target.parentElement;
                          if (iconContainer) {
                            const icon = document.createElement('div');
                            icon.innerHTML = `<${member.icon.name} size={48} style={{ color: '${member.color}' }} />`;
                            iconContainer.appendChild(icon);
                          }
                        }}
                      />
                    ) : (
                      <member.icon size={48} style={{ color: member.color }} />
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold mb-2" style={{ color: member.color }}>
                      {member.name}
                    </h3>
                    <p className="text-black dark:text-white font-medium mb-4 text-lg">{member.role}</p>
                    <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-6">
                      {member.fullBio}
                    </p>
                    {member.credentials && (
                      <div className="space-y-2">
                        <h4 className="text-sm font-semibold text-teal-700 dark:text-teal-400 mb-2">Key Experience:</h4>
                        <ul className="space-y-1">
                          {member.credentials.map((credential, i) => (
                            <li key={i} className="text-sm text-gray-600 dark:text-gray-400 flex items-center">
                              <Building size={12} className="mr-2 text-[#14b8a6]" />
                              {credential}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Call to Action */}
        <motion.div 
          className="text-center bg-white dark:bg-black bg-opacity-60 p-12 rounded-xl border border-[#14b8a6]"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <Users size={48} className="mx-auto mb-6 text-[#14b8a6]" />
          <h2 className="text-3xl font-bold mb-4">
            Ready to Join Our Mission?
          </h2>
          <p className="text-xl text-black dark:text-white mb-8 max-w-2xl mx-auto">
            We're building the future of healthcare triage and advocacy. If you're passionate about making a real difference in patient care, we'd love to hear from you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link href="/voices">
              <Button
                className="bg-teal-600 hover:bg-teal-700 hover:text-teal-200 dark:bg-teal-400 dark:hover:bg-teal-300 text-white dark:text-black font-semibold px-8 py-3 hover:shadow-2xl border border-teal-500 hover:border-teal-500 dark:border-teal-500 dark:hover:border-teal-500"
              >
                Share Your Story
              </Button>
            </Link>
            <Button
              variant="outline"
              className="border-[#14b8a6] text-[#14b8a6] hover:bg-[#14b8a6] hover:text-black hover:shadow-2xl"
            >
              Partner With Us
            </Button>
          </div>
        </motion.div>
      </div>
      
      <Footer />
    </div>
  );
}